import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useCan } from '../../stores/session';
import { Series, type Point } from './Series';
import { Verified } from '../../components/ui/Verified';

/**
 * The admin dashboard.
 *
 * ## Ordered by whether it needs a person
 *
 * Attention first, then health, then growth, then the leaderboards. That is the
 * order somebody opening this page cares about, and the reverse of the order it
 * is tempting to build in - totals and top-tens are the easy half and the least
 * useful.
 *
 * ## The half that nothing else reports
 *
 * Failed jobs, stuck uploads, bounced email, queue age. None of these raise an
 * alarm anywhere, and all of them reach a user as "the site is broken" long
 * before anybody reads a log. A dashboard that only showed growth would be a
 * nicer page and a worse tool.
 *
 * Everything is one request - see `AdminOverview` on why that is the right
 * trade for a screen that is either useful all at once or not at all.
 */
interface Overview {
  attention: {
    reports: number;
    dmca: number;
    host_applications: number;
    failed_jobs: number;
    stuck_uploads: number;
  };
  now: {
    rooms_live: number;
    listeners: number;
    tracks_played_today: number;
    plays_today: number;
    signups_today: number;
  };
  totals: {
    users: number;
    suspended: number;
    tracks: number;
    plays: number;
    albums: number;
    follows: number;
    comments: number;
    storage_bytes: number;
  };
  active: { listeners: number; uploaders: number; hosts: number; signups: number; days: number };
  pipeline: { pending: number; processing: number; ready: number; failed: number };
  queue: { queued: number; reserved: number; failed: number; oldest_queued_seconds: number };
  /*
   * Named rather than a Record, because every one of these is rendered by name
   * and a Record makes each read `possibly undefined` at the point of use - so
   * the type would push a `?? 0` onto every line instead of stating the shape
   * once. The server always sends all seven; see `AdminOverview::email()`.
   */
  email: {
    queued: number;
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    suppressed: number;
    failed: number;
    suppressed_total: number;
    days: number;
  };
  economy: {
    held: number;
    spent: number;
    points_awarded: number;
    badges_earned: number;
    queued_submissions: number;
  };
  series: { signups: Point[]; uploads: Point[]; plays: Point[] };
  top_tracks: { id: number; title: string; username: string; display_name: string; verified?: boolean; plays: number }[];
  top_artists: { username: string; display_name: string; verified?: boolean; listeners: number; plays: number }[];
  window_days: number;
}

export function AdminOverviewPage() {
  const { data, isPending } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.get<Overview>('/admin/overview'),
    // Live enough to be worth glancing at, not so live it is a monitor.
    refetchInterval: 60_000,
  });

  if (isPending || !data) return <LoadingPanel label="Reading the room…" />;

  const attention = [
    { label: 'open reports', value: data.attention.reports, to: '/admin/reports' },
    { label: 'copyright claims', value: data.attention.dmca, to: '/admin/dmca' },
    { label: 'host applications', value: data.attention.host_applications, to: '/admin/host-applications' },
    { label: 'failed jobs', value: data.attention.failed_jobs },
    { label: 'stuck uploads', value: data.attention.stuck_uploads },
  ];

  const waiting = attention.reduce((sum, a) => sum + a.value, 0);

  return (
    <div className="flex flex-col gap-6" data-admin-overview>
      {/* ---------------------------------------------------------- */}
      {/* Needs a person                                              */}
      {/* ---------------------------------------------------------- */}
      <section className="flex flex-col gap-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="meta-caps text-faint">needs attention</h2>
          {waiting === 0 && (
            <span className="font-mono text-[10px] text-fresh">nothing waiting</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
          {attention.map((item) => (
            <Attention key={item.label} {...item} />
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Right now                                                   */}
      {/* ---------------------------------------------------------- */}
      <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="on air" value={data.now.rooms_live} tone={data.now.rooms_live > 0 ? 'fresh' : undefined} />
        <Stat label="listening now" value={data.now.listeners} />
        <Stat label="plays today" value={data.now.plays_today} />
        <Stat label="played on air today" value={data.now.tracks_played_today} />
        <Stat label="signups today" value={data.now.signups_today} />
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Growth                                                      */}
      {/* ---------------------------------------------------------- */}
      <section className="grid gap-4 lg:grid-cols-3">
        <Chart title="Signups" sub={`last ${data.window_days} days`}>
          <Series points={data.series.signups} label="signup" tone="fresh" />
        </Chart>
        <Chart title="Uploads" sub={`last ${data.window_days} days`}>
          <Series points={data.series.uploads} label="upload" />
        </Chart>
        <Chart title="Plays" sub={`last ${data.window_days} days`}>
          <Series points={data.series.plays} label="play" />
        </Chart>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Health                                                      */}
      {/* ---------------------------------------------------------- */}
      <section className="grid gap-4 lg:grid-cols-4">
        <Panel title="Queue" sub="what the workers have left">
          <Row label="waiting" value={data.queue.queued} />
          <Row label="in flight" value={data.queue.reserved} />
          <Row label="failed" value={data.queue.failed} bad={data.queue.failed > 0} />
          {/* The number that matters. A depth of forty is fine if it is
              draining; a depth of three is not if the oldest has waited an
              hour, because that means nothing is draining at all. */}
          <Row
            label="oldest waiting"
            value={duration(data.queue.oldest_queued_seconds)}
            bad={data.queue.oldest_queued_seconds > 600}
          />
        </Panel>

        <Panel title="Transcodes" sub="every track, by state">
          <Row label="ready" value={data.pipeline.ready} />
          <Row label="processing" value={data.pipeline.processing} />
          <Row label="pending" value={data.pipeline.pending} />
          {/* A rejected file, not broken machinery - the worker looked at it and
              said no. Different from a failed job, and a different fix. */}
          <Row label="rejected" value={data.pipeline.failed} bad={data.pipeline.failed > 0} />
        </Panel>

        <Panel title="Email" sub={`last ${data.email.days} days`}>
          <Row label="delivered" value={data.email.delivered} />
          <Row label="sent" value={data.email.sent} />
          {/* The two figures that quietly end a platform's ability to send
              password resets, and which nothing else here reports. */}
          <Row label="bounced" value={data.email.bounced} bad={data.email.bounced > 0} />
          <Row label="complained" value={data.email.complained} bad={data.email.complained > 0} />
          <Row label="suppressed" value={data.email.suppressed_total} />
        </Panel>

        <IrateTechPanel />
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Scale and activity                                          */}
      {/* ---------------------------------------------------------- */}
      <section className="grid gap-4 lg:grid-cols-3">
        <Panel title="Totals" sub="all time">
          <Row label="people" value={data.totals.users} />
          <Row label="suspended" value={data.totals.suspended} bad={data.totals.suspended > 0} />
          <Row label="tracks" value={data.totals.tracks} />
          <Row label="albums" value={data.totals.albums} />
          <Row label="plays" value={data.totals.plays} />
          <Row label="follows" value={data.totals.follows} />
          <Row label="comments" value={data.totals.comments} />
          {/* The only figure here that turns into a bill. */}
          <Row label="originals stored" value={bytes(data.totals.storage_bytes)} />
        </Panel>

        <Panel title="Active" sub={`last ${data.active.days} days`}>
          {/* Totals grow forever and flatter. These can go down, which is what
              makes them the ones worth watching. */}
          <Row label="people who listened" value={data.active.listeners} />
          <Row label="people who uploaded" value={data.active.uploaders} />
          <Row label="people who hosted" value={data.active.hosts} />
          <Row label="new accounts" value={data.active.signups} />
        </Panel>

        <Panel title="Economy" sub="boosts, credits and badges">
          <Row label="submissions queued" value={data.economy.queued_submissions} />
          <Row label="earned boosts held" value={data.economy.held} />
          <Row label="earned boosts spent" value={data.economy.spent} />
          <Row label="discovery points awarded" value={data.economy.points_awarded} />
          <Row label="badges earned" value={data.economy.badges_earned} />
        </Panel>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Who is moving                                               */}
      {/* ---------------------------------------------------------- */}
      <section className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most played" sub={`last ${data.active.days} days`}>
          {data.top_tracks.length === 0 ? (
            <Empty>Nothing played yet this week.</Empty>
          ) : (
            data.top_tracks.map((t) => (
              <Row
                key={t.id}
                label={
                  <span className="truncate">
                    {t.title}{' '}
                    <span className="text-ghost">
                      · <Link to={`/${t.username}`} className="hover:underline">{t.display_name}</Link>
                    </span>
                  </span>
                }
                value={t.plays}
              />
            ))
          )}
        </Panel>

        <Panel title="Reaching people" sub={`distinct listeners, last ${data.active.days} days`}>
          {data.top_artists.length === 0 ? (
            <Empty>Nobody has been heard yet this week.</Empty>
          ) : (
            data.top_artists.map((a) => (
              <Row
                key={a.username}
                label={
                  <Link to={`/${a.username}`} className="truncate hover:underline">
                    {a.display_name}
                    {a?.verified && <Verified />}
                  </Link>
                }
                value={`${a.listeners.toLocaleString()} / ${a.plays.toLocaleString()} plays`}
              />
            ))
          )}
        </Panel>
      </section>
    </div>
  );
}

/**
 * A queue with a person at the end of it.
 *
 * Zero is the good state and looks like it - grey and quiet. Anything above
 * zero is the only thing on the page that shouts, because it is the only thing
 * asking somebody to do something.
 */
function Attention({ label, value, to }: { label: string; value: number; to?: string }) {
  const body = (
    <Card
      className={`flex flex-col gap-1 p-4 transition-colors ${
        value > 0 ? 'border-hot-edge bg-hot-wash' : ''
      } ${to ? 'hover:border-line-strong' : ''}`}
    >
      <span className={`text-2xl font-bold ${value > 0 ? 'text-hot-text' : 'text-soft'}`}>
        {value.toLocaleString()}
      </span>
      <span className="meta-caps text-faint">{label}</span>
    </Card>
  );

  return to ? <Link to={to}>{body}</Link> : body;
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'fresh' }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className={`text-2xl font-bold ${tone === 'fresh' ? 'text-fresh' : 'text-ink'}`}>
        {value.toLocaleString()}
      </span>
      <span className="meta-caps text-faint">{label}</span>
    </Card>
  );
}

function Chart({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <span className="meta-caps text-faint">{sub}</span>
      </div>
      {children}
    </Card>
  );
}

function Panel({ title, sub, children }: { title: React.ReactNode; sub: string; children: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <span className="meta-caps text-faint">{sub}</span>
      </div>
      <div className="flex flex-col">{children}</div>
    </Card>
  );
}

function Row({
  label,
  value,
  bad = false,
}: {
  label: React.ReactNode;
  value: number | string;
  bad?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-2 last:border-0">
      <span className="min-w-0 text-[13px] text-soft">{label}</span>
      <span
        className={`shrink-0 font-mono text-[13px] tabular-nums ${bad ? 'text-hot-text' : 'text-ink'}`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
    </div>
  );
}

/**
 * The moderation and audio vendor, in the health row.
 *
 * Its own query, because it is on a different permission from the rest of
 * the page and reads a third party: somebody without `analytics.view` sees
 * three panels and no gap, and the vendor being slow does not hold the page.
 * The full picture is on the IR8 page; this is the four numbers that say
 * whether it is working.
 */
function IrateTechPanel() {
  const can = useCan('analytics.view');

  const { data } = useQuery({
    queryKey: ['admin-iratetech', 30],
    queryFn: () =>
      api.get<{
        available: boolean;
        reason: string | null;
        account: {
          today: { status: string; n: number }[];
          queue: { queued: number; running: number };
          requests_24h: { server_errors: number; avg_ms: number };
        } | null;
        moderation: { degraded: number } | null;
      }>('/admin/iratetech/overview?days=30'),
    enabled: can,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (!can) return null;

  if (!data?.available || !data.account || !data.moderation) {
    return (
      <Panel title="IR8" sub="moderation and audio">
        <Empty>
          {data?.reason === 'not_configured' ? (
            'Not connected. Set IRATETECH_ADMIN_API_KEY to read it.'
          ) : (
            <>
              No answer from the service.{' '}
              <Link to="/admin/ir8" className="text-accent hover:underline">
                Details
              </Link>
            </>
          )}
        </Empty>
      </Panel>
    );
  }

  const ok = data.account.today.filter((t) => t.status === 'succeeded').reduce((n, t) => n + t.n, 0);
  const bad = data.account.today.filter((t) => t.status === 'failed').reduce((n, t) => n + t.n, 0);

  return (
    <Panel
      title={
        <Link to="/admin/ir8" className="hover:underline">
          IR8
        </Link>
      }
      sub="moderation and audio"
    >
      <Row label="succeeded today" value={ok + bad > 0 ? `${Math.round((ok / (ok + bad)) * 100)}%` : '—'} bad={bad > 0} />
      <Row label="in the queue" value={data.account.queue.queued} bad={data.account.queue.queued > 20} />
      <Row label="server errors, 24h" value={data.account.requests_24h.server_errors} bad={data.account.requests_24h.server_errors > 0} />
      <Row label="degraded verdicts" value={data.moderation.degraded} bad={data.moderation.degraded > 5} />
      <Row label="average answer" value={`${data.account.requests_24h.avg_ms}ms`} />
    </Panel>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-3 text-[13px] text-soft">{children}</p>;
}

/** Seconds as something readable. Zero is "—", because nothing is waiting. */
function duration(seconds: number): string {
  if (seconds <= 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;

  return `${(seconds / 3600).toFixed(1)}h`;
}

function bytes(n: number): string {
  if (n === 0) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));

  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
